<!-- monitoring-frontend/src/components/DashboardView.vue -->

<template>
  <v-container fluid class="crypto-container">
    <v-card class="crypto-card">
      <v-card-title class="crypto-title">
        Мониторинг торговых пар (События и состояния)
      </v-card-title>
      <v-card-text class="crypto-card-text">
        <!-- Форма фильтрации (без изменений) -->
        <v-form @submit.prevent="applyFilters" class="crypto-form">
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-select
                v-model="filters.event_type"
                :items="eventTypes"
                label="Тип события"
                outlined
                dense
                class="crypto-select"
                :class="{'light-text': filters.event_type === 'Все события'}"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-range-slider
                v-model="dayRange"
                :min="0"
                :max="30"
                step="1"
                label="Диапазон дней"
                ticks
                thumb-label="always"
                class="crypto-slider"
              ></v-range-slider>
              <div class="crypto-slider-text">
                {{ dayRange[0] }} - {{ dayRange[1] }} дней назад
              </div>
            </v-col>

            <v-col cols="12" class="text-right">
              <v-btn color="primary" type="submit" class="crypto-btn">Фильтровать</v-btn>
            </v-col>
          </v-row>
        </v-form>

        <v-divider class="crypto-divider"></v-divider>

        <!-- Сводная статистика (без изменений) -->
        <v-card flat class="crypto-stat-card">
          <v-card-title class="crypto-subtitle">Статистика событий</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col v-for="item in friendlyEventSummary" :key="item.type" cols="6" sm="3">
                <div class="crypto-stat-item">{{ item.type }}: {{ item.count }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-divider class="crypto-divider"></v-divider>
        <v-col cols="12" sm="4">
              <v-select
                v-model="pumpDurationThreshold"
                :items="pumpDurationThresholdOptions"
                item-title="text"
                item-value="objectValue"
                label="Длительность пампа"
                outlined
                dense
                class="crypto-select"
                :class="{'light-text': pumpDurationThreshold.text === 'Все'}"
              ></v-select>
            </v-col>
        <!-- Длительность пампа (без изменений) -->
        <v-card flat class="crypto-stat-card">
          <v-card-title class="crypto-subtitle">Длительность пампа</v-card-title>
          <v-card-text>
            <v-row dense v-if="Object.keys(filteredPumpDurations).length">
              <v-col v-for="(durations, pair) in filteredPumpDurations" :key="pair" cols="12" sm="4">
                <div class="crypto-pair-title">{{ pair }}</div>
                <ul class="crypto-pair-list">
                  <li v-for="(d, index) in durations" :key="index">{{ d }}</li>
                </ul>
              </v-col>
            </v-row>
            <div v-else class="crypto-empty">Нет данных по фильтру</div>
          </v-card-text>
        </v-card>

        <v-divider class="crypto-divider"></v-divider>

        <!-- Таблица событий для десктопов -->
        <div class="desktop-table">
          <v-data-table
            :headers="headers"
            :items="filteredEvents"
            class="crypto-table"
            dense
            hide-default-footer
          >
          <template #[`item.event_time`]="{ item }">
            {{ new Date(item.event_time * 1000).toLocaleString() }}
          </template>
          <template #[`item.trading_pair`]="{ item }">
            {{ item.trading_pair.symbol }}
          </template>
          </v-data-table>
        </div>

        <!-- Карточки событий для мобильных -->
        <div class="mobile-cards">
          <v-card
            v-for="event in filteredEvents"
            :key="event.event_time + event.trading_pair.symbol"
            class="crypto-event-card"
            flat
          >
            <v-card-text class="crypto-event-text">
              <div class="crypto-event-row">
                <span class="crypto-event-label">Пара:</span>
                <span>{{ event.trading_pair.symbol }}</span>
              </div>
              <div class="crypto-event-row">
                <span class="crypto-event-label">Событие:</span>
                <span>{{ event.event_type }}</span>
              </div>
              <div class="crypto-event-row">
                <span class="crypto-event-label">Время:</span>
                <span>{{ new Date(event.event_time * 1000).toLocaleString() }}</span>
              </div>
              <div class="crypto-event-row" v-if="event.event_price">
                <span class="crypto-event-label">Цена:</span>
                <span>{{ event.event_price }}</span>
              </div>
              <div class="crypto-event-row" v-if="event.description">
                <span class="crypto-event-label">Описание:</span>
                <span>{{ event.description }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import api from '@/api'

export default {
  name: 'DashboardView',
  data() {
    return {
      events: [],
      tradingPairStates: [],
      loading: true,
      filters: {
        event_type: 'Все события'
      },
      dayRange: [0, 1],
      pumpDurationThreshold: { text: 'Все', min: 0, max: Infinity },
      pumpDurationThresholdOptions: [
        { text: 'Все', objectValue: { text: 'Все', min: 0, max: Infinity } },
        { text: '0-1 минута', objectValue: { text: '0-1 минута', min: 0, max: 60 } },
        { text: '1-5 минут', objectValue: { text: '1-5 минут', min: 60, max: 300 } },
        { text: '5-10 минут', objectValue: { text: '5-10 минут', min: 300, max: 600 } },
        { text: '10-30 минут', objectValue: { text: '10-30 минут', min: 600, max: 1800 } },
        { text: '1+ час', objectValue: { text: '1+ час', min: 3600, max: Infinity } }
      ],
      eventTypes: ['Все события', 'pump_start', 'not_worked_out', 'pump_end', 'baseline_set', 'new_peak'],
      filteredEvents: [],
      headers: [
        { text: 'Торговая пара', value: 'trading_pair.symbol' },
        { text: 'Тип события', value: 'event_type' },
        { text: 'Время события', value: 'event_time' },
        { text: 'Цена', value: 'event_price' },
        { text: 'Описание', value: 'description' }
      ],
      stateHeaders: [
        { text: 'Торговая пара', value: 'trading_pair.symbol' },
        { text: 'Памп активен', value: 'pump_active' },
        { text: 'Пик', value: 'peak' },
        { text: 'MA', value: 'last_ma' },
        { text: 'Последнее обновление', value: 'last_updated' },
        { text: 'Начало пампа', value: 'last_pump_start' },
        { text: 'Длительность пампа', value: 'last_pump_duration' }
      ]
    }
  },
  computed: {
    eventSummary() {
      // Считаем сводку на основе отфильтрованных событий
      const summary = {
        all: 0,
        pump_start: 0,
        new_peak: 0,
        pump_end: 0,
        baseline_set: 0,
        not_worked_out: 0
      }
      this.filteredEvents.forEach(event => {
        summary.all++
        if (summary[event.event_type] !== undefined) {
          summary[event.event_type]++
        }
      })
      return summary
    },
    friendlyEventSummary() {
      const mapping = {
        pump_start: 'Начало пампа',
        new_peak: 'Новый пик',
        pump_end: 'Отработка',
        not_worked_out: 'Не отработано за 24ч'
      }
      let friendly = []
      if (this.eventSummary.all > 0) {
        friendly.push({ type: 'Все события', count: this.eventSummary.all })
      }
      for (const key in mapping) {
        if (this.eventSummary[key] > 0) {
          friendly.push({ type: mapping[key], count: this.eventSummary[key] })
        }
      }
      return friendly
    },
    rawPumpDurations() {
      const durations = {};
      const grouped = {};
      this.filteredEvents.forEach(event => {
        const symbol = event.trading_pair ? event.trading_pair.symbol : event.symbol;
        if (!grouped[symbol]) grouped[symbol] = [];
        grouped[symbol].push(event);
      });
      for (const symbol in grouped) {
        const events = grouped[symbol].sort((a, b) => a.event_time - b.event_time);
        let pumpDurations = [];
        for (let i = 0; i < events.length; i++) {
          if (events[i].event_type === 'pump_start') {
            const startTime = events[i].event_time;
            for (let j = i + 1; j < events.length; j++) {
              if (events[j].event_type === 'pump_end') {
                pumpDurations.push(events[j].event_time - startTime);
                break;
              }
            }
          }
        }
        durations[symbol] = pumpDurations;
      }
      return durations;
    },
    filteredPumpDurations() {
      const threshold = this.pumpDurationThreshold;
      const result = {};
      const raw = this.rawPumpDurations;
      for (const symbol in raw) {
        const filtered = raw[symbol].filter(d => d >= threshold.min && d < threshold.max);
        if (filtered.length) {
          result[symbol] = filtered.map(d => this.formatDuration(Math.floor(d)));
        }
      }
      return result;
    }
  },
  methods: {
    async fetchEvents() {
      try {
        const res = await api.get('events/');
        this.events = res.data;
      } catch (error) {
        console.error("Ошибка получения событий:", error);
      } finally {
        this.applyFilters();
      }
    },
    async fetchStates() {
      try {
        const res = await api.get('trading-pair-states/');
        this.tradingPairStates = res.data;
      } catch (error) {
        console.error("Ошибка получения состояний:", error);
      }
    },
    applyFilters() {
      const now = Date.now() / 1000;
      const start_time = now - this.dayRange[1] * 86400;
      const end_time = now - this.dayRange[0] * 86400;

      // Первичная фильтрация по диапазону дат и типу события
      let filtered = this.events.filter(event => {
        return event.event_time >= start_time && event.event_time <= end_time;
      });
      if (this.filters.event_type && this.filters.event_type !== 'Все события') {
        filtered = filtered.filter(event => event.event_type === this.filters.event_type);
      }

      // Дополнительная фильтрация по длительности пампа (если выбран соответствующий фильтр)
      // Предполагаем, что фильтрация по длительности имеет смысл для pump_start, для которых можно вычислить длительность.
      if (this.pumpDurationThreshold.text !== 'Все') {
        filtered = filtered.filter(event => {
          if (event.event_type !== 'pump_start') {
            // Если событие не pump_start, его оставляем без проверки по длительности
            return true;
          }
          // Найдем для pump_start соответствующий pump_end (событие с тем же trading_pair и временем больше)
          const pumpEndEvent = this.events.find(e =>
            e.trading_pair.symbol === event.trading_pair.symbol &&
            e.event_type === 'pump_end' &&
            e.event_time > event.event_time
          );
          if (!pumpEndEvent) {
            // Если pump_end не найдено, пропускаем это событие (либо можно вернуть true, если хотим видеть и незавершенные)
            return false;
          }
          const duration = pumpEndEvent.event_time - event.event_time;
          return duration >= this.pumpDurationThreshold.min && duration < this.pumpDurationThreshold.max;
        });
      }

      this.filteredEvents = filtered;
    },
    formatDuration(seconds) {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      let result = '';
      if (hrs > 0) result += hrs + ' ч. ';
      if (mins > 0) result += mins + ' мин. ';
      result += secs + ' сек';
      return result;
    },
    refreshData() {
      this.fetchEvents();
      this.fetchStates();
    }
  },
  mounted() {
    this.refreshData();
    this.intervalId = setInterval(() => {
      this.refreshData();
    }, 15000);
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  }
}
</script>

<style scoped>

.light-text .v-input__control {
  color: #b0b0b0 !important;
}

.light-text .v-input__control {
  color: #b0b0b0 !important; /* Светлый оттенок */
}

::v-deep .crypto-slider .v-slider__label {
  color: #b0b0b0;
}

.crypto-slider >>> .v-slider__label {
  color: #ffffff !important;
  font-weight: 600;
}

.crypto-container {
  background: linear-gradient(135deg, #1a1a1a, #0d0d0d);
  padding: 16px;
  min-height: 100vh;
}

.crypto-card {
  background: #252525;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.6);
  overflow: hidden;
}

.crypto-title {
  font-family: 'Orbitron', sans-serif;
  font-size: 1.6rem;
  color: #00ffcc;
  padding: 12px 16px;
  background: #1e1e1e;
}

.crypto-card-text {
  padding: 12px 16px;
}

.crypto-form .v-row {
  margin-bottom: 8px;
}

.crypto-select, .crypto-slider {
  margin-bottom: 8px;
}

.crypto-select .v-input__control {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
}



.crypto-btn {
  background: #00ffcc !important;
  color: #000 !important;
  font-weight: 600;
  padding: 6px 16px;
}

.crypto-divider {
  margin: 12px 0;
  border-color: #444;
}

.crypto-stat-card {
  background: #2c2c2c;
  border-radius: 6px;
  padding: 8px;
}

.crypto-subtitle {
  font-size: 1.2rem;
  color: #00ffcc;
  padding: 8px 0;
}

.crypto-stat-item {
  font-size: 0.95rem;
  color: #ddd;
  padding: 4px 0;
}

.crypto-pair-title {
  font-weight: 600;
  color: #00ffcc;
  margin-bottom: 4px;
}

.crypto-pair-list {
  padding-left: 16px;
  margin-bottom: 8px;
  color: #ccc;
  font-size: 0.9rem;
}

.crypto-empty {
  color: #888;
  font-size: 0.95rem;
  text-align: center;
  padding: 8px;
}

.crypto-table {
  background: #222;
  color: #ddd;
}

.crypto-table th {
  background: #1e1e1e !important;
  color: #00ffcc !important;
  font-size: 0.9rem !important;
}

.crypto-table td {
  font-size: 0.85rem;
  padding: 6px !important;
}

/* Мобильные карточки */
.mobile-cards {
  display: none;
}

.crypto-event-card {
  background: #2c2c2c;
  margin-bottom: 8px;
  border-radius: 6px;
}

.crypto-event-text {
  padding: 8px;
}

.crypto-event-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #ddd;
  margin-bottom: 4px;
}

.crypto-event-label {
  color: #00ffcc;
  font-weight: 500;
  margin-right: 8px;
}

.crypto-slider .v-slider__track-background {
  background: rgba(255, 255, 255, 0.4) !important; /* Светлый серый */
  height: 5px !important;
}



.crypto-form {
  background: rgba(255, 255, 255, 0.15); /* Светлее, чтобы выделялась */
  padding: 12px;
  border-radius: 6px;
}

.crypto-slider .v-slider__track-fill {
  background: #00ffcc !important; /* Активная зона */
  height: 4px !important; /* Делаем трек толще */
}

.crypto-slider .v-slider__track-fill {
  background: #00ffcc !important; /* Активная зона */
}

.crypto-select .v-input__control,
.crypto-slider {
  color: #fff !important;
}

.crypto-slider .v-slider__thumb {
  background: #00ffcc !important; /* Яркий цвет */
  border: 2px solid white !important; /* Белая обводка */
  width: 16px !important; /* Чуть больше */
  height: 16px !important;
  box-shadow: 0px 0px 6px rgba(0, 255, 204, 0.8) !important; /* Эффект свечения */
}



.v-label,
.crypto-slider-text {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.crypto-btn {
  background: #00ffcc !important;
  color: #000 !important;
  font-weight: 600;
  padding: 6px 16px;
}

.light-text .v-input__control {
  color: #fff !important;
}

.v-label {
  color: #fff !important; /* Чтобы текст заголовков был видимым */
}

.v-label, 
.crypto-slider-text, 
.crypto-form .v-input__control {
  color: #ffffff !important; /* Белый цвет текста */
  font-weight: 500;
}

::v-deep .v-label {
  color: #ffffff !important;
  font-weight: 500;
}

::v-deep .v-input__control {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

::v-deep .v-slider__track-background {
  background: rgba(255, 255, 255, 0.4) !important;
}

::v-deep .v-slider__thumb {
  background: #00ffcc !important;
  border: 2px solid white;
}


/* Адаптация */
@media (min-width: 601px) {
  .mobile-cards {
    display: none;
  }
  .desktop-table {
    display: block;
  }
}

@media (max-width: 600px) {
  .mobile-cards {
    display: block;
  }
  .desktop-table {
    display: none;
  }

  .crypto-title {
    font-size: 1.3rem;
  }
  .crypto-card-text {
    padding: 8px;
  }
  .crypto-btn {
    background: #00ffcc !important;
    color: #000 !important;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 6px;
    text-transform: uppercase;
  }
}
</style>